import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ServiceLandingData } from '../../../mocks/service-landings';
import { getStoredUTM, utmToParams } from '../../../hooks/useUTM';

interface SLContactFormProps {
  data: ServiceLandingData;
}

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const FORM_URL = 'https://readdy.ai/api/form/d73osaqvu4krcbn3ar6g';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function SLContactForm({ data }: SLContactFormProps) {
  const { ref, inView } = useInView();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', gdpr: false });
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      if (name === 'message') setCharCount(value.length);
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.gdpr || charCount > 500) return;
    setStatus('sending');
    try {
      const utm = getStoredUTM();
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: data.hero.badge,
        message: form.message,
        source: `Landing page - ${data.hero.badge}`,
        ...utmToParams(utm),
      });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        navigate(`/dakujeme?service=${encodeURIComponent(data.hero.badge)}`);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="kontaktny-formular" className="py-24 bg-zinc-950 relative overflow-hidden">
      {/* BG accent */}
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.08) 0%, transparent 60%)' }}
      />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">

          {/* Left - text */}
          <div
            className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-mail-send-line text-orange-400 text-sm" />
              <span className="text-zinc-300 text-sm font-semibold">Napíšte nám</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4 leading-tight">
              {data.cta.heading}
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed mb-8">
              {data.cta.subheading}
            </p>

            {/* Assurances */}
            <div className="space-y-4">
              {[
                { icon: 'ri-time-line', title: 'Odpovieme do 24 hodín', text: 'Každú správu berie vážne reálny človek, nie automat.' },
                { icon: 'ri-lock-line', title: 'Bez záväzkov', text: 'Konzultácia vás k ničomu nezaväzuje. Len si porozprávame.' },
                { icon: 'ri-eye-off-line', title: 'Vaše údaje sú v bezpečí', text: 'Nikdy nebudeme zdieľať vaše kontaktné informácie.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-800/80 border border-zinc-700/60">
                    <i className={`${item.icon} text-orange-400 text-base`} />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{item.title}</p>
                    <p className="text-zinc-500 text-xs mt-0.5">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - form */}
          <div
            className={`transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-7">
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-5">
                    <i className="ri-check-line text-emerald-400 text-3xl" />
                  </div>
                  <h3 className="text-white text-2xl font-black mb-2">Odoslané!</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed max-w-xs">
                    Ďakujeme za záujem. Ozveme sa vám čo najskôr - zvyčajne do 24 hodín.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id={`sl-contact-form-${data.slug}`}
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-zinc-400 text-xs font-semibold mb-1.5 block">Meno a priezvisko *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Ján Novák"
                        className="w-full bg-zinc-800/60 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-500/50 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs font-semibold mb-1.5 block">Telefón</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+421 9xx xxx xxx"
                        className="w-full bg-zinc-800/60 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-500/50 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-zinc-400 text-xs font-semibold mb-1.5 block">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="jan@firma.sk"
                      className="w-full bg-zinc-800/60 border border-zinc-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-500/50 transition-all duration-200"
                    />
                  </div>

                  <input type="hidden" name="service" value={data.hero.badge} />
                  <input type="hidden" name="source" value={`Landing page - ${data.hero.badge}`} />

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-zinc-400 text-xs font-semibold">Opíšte váš zámer *</label>
                      <span className={`text-xs ${charCount > 450 ? 'text-red-400' : 'text-zinc-600'}`}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={4}
                      placeholder={`Napíšte nám o vašom projekte, čo chcete dosiahnuť a kde sa teraz nachádzate...`}
                      className="w-full bg-zinc-800/60 border border-zinc-700 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-500/50 transition-all duration-200 resize-none"
                    />
                  </div>

                  {/* GDPR */}
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input
                        type="checkbox"
                        name="gdpr"
                        checked={form.gdpr}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all duration-200 ${form.gdpr ? 'bg-orange-500 border-orange-500' : 'bg-zinc-800 border-zinc-600 group-hover:border-zinc-500'}`}>
                        {form.gdpr && <i className="ri-check-line text-white text-xs" />}
                      </div>
                    </div>
                    <p className="text-zinc-500 text-xs leading-relaxed">
                      Súhlasím so spracovaním osobných údajov podľa{' '}
                      <a href="/ochrana-sukromia" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">
                        zásad ochrany súkromia
                      </a>
                      .
                    </p>
                  </label>

                  {status === 'error' && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-xs px-4 py-3 rounded-xl">
                      Odosielanie zlyhalo. Skúste to znova alebo nás kontaktujte priamo.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || !form.gdpr}
                    className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-white font-bold py-3.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-sm"
                  >
                    {status === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Odosielam...
                      </>
                    ) : (
                      <>
                        Odoslať dopyt
                        <i className="ri-send-plane-line" />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
